import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { apiFetch } from '@/utils'
import { useState } from 'react'
import { useLocation } from 'wouter'
import logo from '@/assets/logo-login.svg';
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Loader2 } from 'lucide-react'

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('')

    const { toast } = useToast()
    const [, navigate] = useLocation()

    const [isSending, setIsSending] = useState(false)

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    const onSubmit = async () => {
        setIsSending(true)
        try {
            const response = await apiFetch('/v1/users/password-reset', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });

            const result = await response.json();

            if (!result.success) {
                toast({
                    title: 'Error',
                    description: `Failed to send password reset email: ${result.error.message}`,
                    variant: 'destructive'
                })
            } else {
                toast({
                    title: 'Success',
                    description: 'Password reset link sent to your email'
                })
                navigate('/login')
            }


        } catch (error: any) {
            console.error('Error sending password reset email:', error)
            toast({
                title: 'Failed to send password reset email',
                description: error.message,
                variant: 'destructive'
            })
        }
        setIsSending(false)
    }

    return (
        <div className='flex justify-center'>
            <div className="min-h-auto h-full flex items-end justify-between login-page flex-col relative w-full max-w-5xl">
                <div className="flex flex-col justify-center items-center w-full h-[calc(100vh-294px)]">
                    <img src={logo} alt="" />
                    <p className="text-[20px] leading-6 font-bold text-[#ffffff] font-montserrat mt-1 mb-1">
                        SECH
                    </p>
                    <p className="text-xs leading-[14px] font-montserrat font-medium text-[#FFFFFF]">
                        Digital Wallet
                    </p>
                </div>
                <div className="max-w-[1024px] w-full">
                    <Card className="bg-[#2F2F32] border-0 p-6 rounded-tl-[36px] rounded-tr-[36px] rounded-b-none">
                        <div className="text-center mb-6">
                            <h1 className="text-[20px] leading-[26px] font-semibold text-[#ffffff] font-figtree">Forgot Password</h1>
                            <p className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF] mt-2">
                                Enter the email address connected to your account, and we'll send
                                you instructions to reset your password.
                            </p>
                        </div>

                        <div className="mb-6">
                            <Label className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">
                                Email
                            </Label>

                            <Input
                                type="email"
                                autoComplete="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={handleEmailChange}
                                className="bg-[#212124] border-0 h-[42px] rounded-[8px] text-[#ffffff]
                            focus-visible:outline-none
                            focus-visible:ring-2
                            focus-visible:ring-[#6B6565]
                            focus-visible:ring-offset-0"
                            />
                        </div>
                        <Button onClick={onSubmit} className="w-full h-[42px] text-sm leading-[14px] font-medium font-figtree mb-6
                        text-[#ffffff] rounded-[21px] bg-[#EF4444]" disabled={isSending}>
                            {isSending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </Button>

                        <div className="text-center">
                            <p className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF]">
                                Remember your password?{' '}
                                <Button
                                    variant="link"
                                    className="p-0 h-auto font-semibold font-figtree text-xs leading-4
                            text-[#EF4444] hover:text-[#DC2626]"
                                    onClick={() => navigate('/login')}>
                                    Log in
                                </Button>
                            </p>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    )
}
